import {
	FuturesMarginType,
	FuturesMarketAsset,
	FuturesMarketKey,
	FuturesOrderType,
	FuturesOrderTypeDisplay,
	MarginTransfer,
	NetworkId,
	PerpsMarketV2,
	PerpsMarketV3,
	PositionSide,
	PotentialTradeStatus,
} from '@kwenta/sdk/types'
import Wei from '@kwenta/wei'

import { PricesInfo } from 'state/prices/types'
import { QueryStatus } from 'state/types'

import { PerpsProvider } from '../types'

type excludedOptions = typeof FuturesMarginType.ISOLATED_MARGIN_LEGACY
export type AppFuturesMarginType = Exclude<FuturesMarginType, excludedOptions>

export type FuturesAccountData = {
	marketMarginTransfers?: MarginTransfer[]
}

export type DelayedOrderWithDetails<T = Wei> = {
	account: string
	marketAddress: string
	market: string
	asset: FuturesMarketAsset
	marketKey: FuturesMarketKey
	size: T
	commitDeposit: T
	keeperDeposit: T
	submittedAtTimestamp: number
	executableAtTimestamp: number
	isOffchain: boolean
	desiredFillPrice: T
	targetRoundId: T | null
	orderType: FuturesOrderTypeDisplay
	side: PositionSide
	isStale?: boolean
	isExecutable?: boolean
	isCancelling?: boolean
}

export type FuturesTradePreviewDetails<T = Wei> = {
	marketKey: FuturesMarketKey
	newSize: T
	sizeDelta: T
	liqPrice: T
	fillPrice: T
	fee: T
	notionalValue: T
	leverage: number
	side: PositionSide
	status: PotentialTradeStatus
	showStatus?: boolean
	statusMessage?: string
	priceImpact: T
	exceedsPriceProtection: boolean
}

export type HistoricalFundingRates = Partial<
	Record<FuturesMarketAsset, { timestamp: string; funding: string }[]>
>

export type FuturesQueryStatuses = {
	markets: QueryStatus
	dailyVolumes: QueryStatus
	positions: QueryStatus
	positionHistory: QueryStatus
	openOrders: QueryStatus
	tradePreview: QueryStatus
	account: QueryStatus
	trades: QueryStatus
	marginTransfers: QueryStatus
	historicalFundingRates: QueryStatus
	futuresFees: QueryStatus
	futuresFeesForAccount: QueryStatus
	orderHistory: QueryStatus
	globalTradesHistory: QueryStatus
	selectedTraderPositionHistory: QueryStatus
}

export type SnxPerpsV2QueryStatuses = FuturesQueryStatuses & {
	snxV2BalanceInfo: QueryStatus
	swapDepositBalanceQuote: QueryStatus
	swapDepositQuote: QueryStatus
	delegatesForSnxV2Account: QueryStatus
	subAccountsForSnxV2Account: QueryStatus
}

export type TradeSizeInputs<T = Wei> = {
	nativeSize: T
	susdSize: T
}

export type TradePanelInputs<T = Wei> = TradeSizeInputs<T> & {
	stopLossPrice?: T
	takeProfitPrice?: T
	marginDelta?: T
	orderType: FuturesOrderType
	orderPrice: {
		price?: T
		invalidLabel?: string
	}
}

export type EditPositionInputs<T = Wei> = {
	nativeSizeDelta: T
	marginDelta: T
}

export type ClosePositionInputs<T = Wei> = {
	nativeSizeDelta: T
	price?: {
		value?: string | undefined | null
		invalidLabel: string | undefined | null
	}
	orderType: FuturesOrderType
}

export type PreviewAction = 'edit' | 'trade' | 'close'

export type TradePreviewParams = {
	orderPrice: Wei
	sizeDelta: Wei
	action: PreviewAction
	isConditional?: boolean
}

export type SnxPerpsV3TradePreviewParams = TradePreviewParams & {
	market: PerpsMarketV3
	marketPrice: Wei
	indexPrice: Wei
}

export type SnxPerpsV2TradePreviewParams = TradePreviewParams & {
	market: PerpsMarketV2
	marginDelta: Wei
	hasStopOrTakeProfit?: boolean
}

export type DebouncedSMPreviewParams = SnxPerpsV2TradePreviewParams & {
	debounceCount: number
}

export type DebouncedCMPreviewParams = SnxPerpsV3TradePreviewParams & {
	debounceCount: number
}

export type AccountContext = {
	provider: PerpsProvider
	network: NetworkId
	wallet: string
	cmAccount?: string
}

export type FundingRate<T = Wei> = {
	asset: FuturesMarketKey
	fundingTitle: string
	fundingRate: T | null
}

export type MarkPrices<T = Wei> = Partial<Record<FuturesMarketKey, T>>

export type MarkPriceInfos<T = Wei> = Partial<Record<FuturesMarketKey, PricesInfo<T>>>

export enum CrossMarginRiskStatus {
	Low = 'low',
	Moderate = 'moderate',
	High = 'high',
}

export type FuturesTransactionType =
	| 'deposit_smart_margin'
	| 'withdraw_smart_margin'
	| 'approve_cross_margin'
	| 'deposit_cross_margin'
	| 'withdraw_cross_margin'
	| 'modify_smart_margin'
	| 'close_smart_margin'
	| 'close_cross_margin'
	| 'cancel_delayed_isolated'
	| 'execute_delayed_isolated'
	| 'close_smart_margin'
	| 'submit_cross_order'
	| 'cancel_cross_margin_order'
	| 'withdraw_keeper_balance'
	| 'deposit_keeper_balance'
	| 'create_cross_margin_account'
	| 'add_delegate'
	| 'remove_delegate'
	| 'delegate_to_smart_margin'
	| 'create_smart_margin_account'
	| 'permit_margin_engine'
